import type { CockpitWidgetsConfigurationItem } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';

/**
 * Cockpit Widget IDs - must match backend CockpitWidgetsConfigurationItem property names
 */
export const CockpitWidgetIds = {
  ASSIGNMENTS_TILE: 'AssignmentsTile',
  DASHBOARD_MY_COURSES: 'DashboardMyCourses',
  DASHBOARD_SCHOOL_LINKS: 'DashboardSchoolLinks',
  DASHBOARD_TIME_TABLE: 'DashboardTimeTable',
  STATISTICS: 'Statistics',
  ANNOUNCEMENTS: 'Announcements',
  NEWS: 'News',
  SUBSTITUTION: 'Substitution',
  GUARDIAN_CHAT: 'GuardianChat',
} as const;

export type CockpitWidgetId = (typeof CockpitWidgetIds)[keyof typeof CockpitWidgetIds];

/**
 * Widgets that are not yet implemented and should be hidden from settings
 */
export const DISABLED_WIDGETS: readonly CockpitWidgetId[] = [
  CockpitWidgetIds.STATISTICS,
] as const;

/**
 * Type guard to check if a key is a valid widget key
 */
export function isCockpitWidgetKey(key: string): key is CockpitWidgetId {
  // Check if the key exists in our defined widget IDs
  const validKeys = Object.values(CockpitWidgetIds);
  return validKeys.includes(key as CockpitWidgetId);
}

/**
 * Safely get widget keys from cockpit settings, filtering out disabled widgets
 */
export function getVisibleWidgetKeys(settings: CockpitWidgetsConfigurationItem | null): string[] {
  if (!settings?.widgets) return [];

  return Object.keys(settings.widgets).filter(
    (key) => !DISABLED_WIDGETS.includes(key as CockpitWidgetId),
  );
}

/**
 * Validates that all constants match the backend interface
 * This function should be called during development to ensure consistency
 * @returns true if all widget IDs are valid, false otherwise
 */
export function validateWidgetConstants(): boolean {
  const definedIds = Object.values(CockpitWidgetIds);

  // Ensure no duplicates
  const uniqueIds = new Set(definedIds);
  if (uniqueIds.size !== definedIds.length) {
    console.error('Duplicate widget IDs found in CockpitWidgetIds');
    return false;
  }

  // Ensure DISABLED_WIDGETS only contains valid IDs
  const invalidDisabledWidgets = DISABLED_WIDGETS.filter((widget) => !definedIds.includes(widget));
  if (invalidDisabledWidgets.length > 0) {
    console.error('Invalid widget IDs in DISABLED_WIDGETS:', invalidDisabledWidgets);
    return false;
  }

  return true;
}

/**
 * Get a human-readable widget name for logging/debugging
 * @todo Is this in use?
 */
export function getWidgetDisplayName(widgetId: CockpitWidgetId): string {
  const displayNames: Record<CockpitWidgetId, string> = {
    [CockpitWidgetIds.ASSIGNMENTS_TILE]: 'Assignments Tile',
    [CockpitWidgetIds.DASHBOARD_MY_COURSES]: 'My Courses',
    [CockpitWidgetIds.DASHBOARD_SCHOOL_LINKS]: 'School Links',
    [CockpitWidgetIds.DASHBOARD_TIME_TABLE]: 'Timetable',
    [CockpitWidgetIds.STATISTICS]: 'Statistics',
    [CockpitWidgetIds.ANNOUNCEMENTS]: 'Announcements',
    [CockpitWidgetIds.NEWS]: 'News',
    [CockpitWidgetIds.SUBSTITUTION]: 'Substitution',
    [CockpitWidgetIds.GUARDIAN_CHAT]: 'Guardian Chat',
  };

  return displayNames[widgetId] || widgetId;
}
